import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { GitMerge, EyeOff, Copy, Phone, Mail, Building2, Loader2, CheckCircle2 } from "lucide-react";
import { api, errMsg } from "@/lib/api";
import { useCrm } from "@/context/CrmContext";
import { StageBadge, ServiceTag } from "@/components/Badges";
import { inrShort, fmtDT } from "@/lib/constants";

const TYPE = {
  SAME_PHONE: { t: "Same phone", icon: Phone, c: "bg-rose-50 text-rose-700" },
  SAME_EMAIL: { t: "Same email", icon: Mail, c: "bg-[#FFF0D9] text-[#A85E00]" },
  SIMILAR_COMPANY: { t: "Similar company", icon: Building2, c: "bg-[#FBF5C9] text-[#7A6A00]" },
};

const Group = ({ g, onDone }) => {
  const [primary, setPrimary] = useState(g.leads[0].id);
  const [sel, setSel] = useState(g.leads.map((l) => l.id));
  const [busy, setBusy] = useState(false);
  const T = TYPE[g.match_type];
  const dups = sel.filter((i) => i !== primary);
  const toggle = (id) => setSel((s) => (s.includes(id) ? s.filter((x) => x !== id) : [...s, id]));

  const merge = async () => {
    setBusy(true);
    try { const { data } = await api.post("/duplicates/merge", { primary_id: primary, duplicate_ids: dups }); toast.success(`Merged ${data.merged} record(s) · ${data.moved.meetings} meetings moved`); onDone(); }
    catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  const dismiss = async () => {
    try { await api.post("/duplicates/dismiss", { lead_ids: g.leads.map((l) => l.id) }); toast.success("Marked as not duplicates"); onDone(); } catch (e) { toast.error(errMsg(e)); }
  };

  return (
    <div data-testid={`dup-group-${g.key}`} className="rounded-3xl border border-neutral-200 bg-white p-5">
      <div className="flex flex-wrap items-center gap-2">
        <span className={`flex items-center gap-1 rounded-full px-3 py-1 text-xs font-semibold ${T.c}`}><T.icon className="h-3.5 w-3.5" />{T.t}</span>
        <span className="font-mono text-xs text-neutral-500">{g.match_value}</span>
        <span className="ml-auto text-xs text-neutral-400">{g.leads.length} records</span>
      </div>
      <div className="mt-4 grid gap-3 md:grid-cols-2">
        {g.leads.map((l) => {
          const isP = primary === l.id;
          const inc = sel.includes(l.id);
          return (
            <div key={l.id} data-testid={`dup-lead-${l.id}`} className={`rounded-2xl border p-4 transition-colors ${isP ? "border-[#1F9E7A] bg-[#E6F5EF]" : inc ? "border-neutral-300" : "border-dashed border-neutral-200 opacity-50"}`}>
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0">
                  <Link to={`/leads/${l.id}`} className="block truncate font-semibold text-[#151515] hover:underline">{l.full_name}</Link>
                  <p className="truncate text-sm text-neutral-500">{l.company_name || "—"}</p>
                </div>
                <StageBadge stage={l.lifecycle_stage} />
              </div>
              <div className="mt-2 space-y-0.5 font-mono text-xs text-neutral-600"><p>{l.phone_e164}</p><p>{l.email || "no email"}</p></div>
              <div className="mt-2 flex flex-wrap items-center gap-1.5"><ServiceTag service={l.service_category} />{l.is_secondary && <span className="rounded-full bg-[#FBF5C9] px-2 py-0.5 text-[10px] font-semibold text-[#7A6A00]">SECONDARY</span>}</div>
              <p className="mt-2 text-[11px] text-neutral-500">{l.assigned_user_name || "Unassigned"} · {inrShort(l.estimated_budget)} · {l.meetings} meetings · {l.activities} activities · added {fmtDT(l.created_at)}</p>
              <div className="mt-3 flex gap-2">
                <button data-testid={`dup-set-primary-${l.id}`} onClick={() => { setPrimary(l.id); if (!inc) toggle(l.id); }} className={`flex h-8 items-center gap-1 rounded-full px-3 text-xs font-semibold ${isP ? "bg-[#1F9E7A] text-white" : "border border-neutral-300 text-neutral-600"}`}>
                  {isP ? <><CheckCircle2 className="h-3.5 w-3.5" />Keep this</> : "Make primary"}
                </button>
                {!isP && <button data-testid={`dup-toggle-${l.id}`} onClick={() => toggle(l.id)} className="h-8 rounded-full border border-neutral-300 px-3 text-xs text-neutral-600">{inc ? "Exclude" : "Include"}</button>}
              </div>
            </div>
          );
        })}
      </div>
      <div className="mt-4 flex flex-wrap gap-2">
        <button data-testid={`dup-merge-btn-${g.key}`} disabled={busy || dups.length === 0} onClick={merge} className="flex h-10 items-center gap-1.5 rounded-full bg-[#232323] px-4 text-sm font-semibold text-white disabled:opacity-40">
          {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <GitMerge className="h-4 w-4 text-[#F5E04F]" />}Merge {dups.length} into primary
        </button>
        <button data-testid={`dup-dismiss-btn-${g.key}`} onClick={dismiss} className="flex h-10 items-center gap-1.5 rounded-full border border-neutral-300 px-4 text-sm text-neutral-600"><EyeOff className="h-4 w-4" />Not duplicates</button>
      </div>
      <p className="mt-2 text-[11px] text-neutral-400">Merging moves meetings, timeline and tasks to the primary, fills its empty fields, keeps extra phones as alternates, then removes the duplicates.</p>
    </div>
  );
};

export default function Duplicates() {
  const { refresh } = useCrm();
  const [groups, setGroups] = useState(null);
  const [filter, setFilter] = useState("ALL");
  const load = () => api.get("/duplicates").then((r) => setGroups(r.data));
  useEffect(() => { load(); }, []);
  const shown = (groups || []).filter((g) => filter === "ALL" || g.match_type === filter);
  const count = (t) => (groups || []).filter((g) => t === "ALL" || g.match_type === t).length;

  return (
    <div className="space-y-5">
      <div><h1 className="font-display text-2xl font-bold text-[#151515] sm:text-3xl">Duplicate leads</h1><p className="text-sm text-neutral-500">Records sharing a phone or email, or with very similar company names.</p></div>
      <div className="flex flex-wrap gap-2">
        {["ALL", "SAME_PHONE", "SAME_EMAIL", "SIMILAR_COMPANY"].map((t) => (
          <button key={t} data-testid={`dup-filter-${t.toLowerCase().replace(/_/g, "-")}`} onClick={() => setFilter(t)} className={`h-9 rounded-full px-4 text-sm font-medium ${filter === t ? "bg-[#232323] text-white" : "border border-neutral-300 bg-white text-neutral-600"}`}>
            {t === "ALL" ? "All" : TYPE[t].t} <span className="ml-1 font-mono text-xs opacity-70">{count(t)}</span>
          </button>))}
      </div>
      {groups === null && <p className="text-neutral-400">Scanning…</p>}
      {groups && shown.length === 0 && (
        <div data-testid="dup-empty" className="flex flex-col items-center gap-2 rounded-3xl border border-dashed border-neutral-300 bg-white p-10 text-center text-neutral-500"><Copy className="h-8 w-8 text-[#1F9E7A]" />No duplicates found — your data is clean.</div>
      )}
      <div className="space-y-4">{shown.map((g) => <Group key={g.key} g={g} onDone={() => { load(); refresh(); }} />)}</div>
    </div>
  );
}
